import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { LocationService } from '../../../../../services/location/location.service';
import { StripeReader } from '../../../../../models/location';
import { AccountService } from '../../../../../services/account/account.service';
import { PaymentService, StripeTerminalPaymentStatus } from '../../../../../services/payment/payment.service';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { PaymentStatus } from '../../../../../models/payment-enums';
import { Payment } from '../../../../../models/payment';
import { OrganizationConfigService } from '../../../../../services/organization-config/organization-config.service';
import {
  SimpleMessageModalComponent
} from '../../../../../components/modals/simple-message-modal/simple-message-modal.component';
import * as liveReaderRefusal from '../live-reader-refusal';

enum ModalState {
  choosingReader,
  initializingPayment,
  waitingForPayment,
  paymentFailed
}

@Component({
  selector: 'app-stripe-terminal-server-driven-payment-modal',
  templateUrl: './stripe-terminal-server-driven-payment-modal.component.html',
  styleUrls: ['./stripe-terminal-server-driven-payment-modal.component.css']
})
export class StripeTerminalServerDrivenPaymentModalComponent implements OnInit, OnDestroy {
  @Input() invoiceId: number;
  @Input() customerId: number;
  @Input() paymentDescription: string;
  @Input() amount?: number;

  @Input() onSuccess: () => void;

  public state = ModalState.choosingReader;

  public readerList: StripeReader[];
  public loadingReaders = true;
  public selectedReader: StripeReader = null;

  public paymentId: number = null;

  public loadingCancelPayment = false;
  public cancelInProgress = false;
  public cancelRefused = false;

  public serverError: number = null;
  public errorMessage = '';
  public liveReaderRefusal: liveReaderRefusal.LiveReaderRefusal = null;

  public checkStatusIntervalHolder: any;

  public ModalState = ModalState;

  constructor(
    public activeModal: NgbActiveModal,
    private accountService: AccountService,
    private locationService: LocationService,
    private paymentService: PaymentService,
    private modalService: NgbModal,
    private orgConfig: OrganizationConfigService
  ) {}

  ngOnInit() {
    this.loadReaders();

    // Check the status of a pending payment every second.
    this.checkStatusIntervalHolder = setInterval(
      () => {
        this.checkPaymentStatus();
      },
      1000
    )
  }

  ngOnDestroy() {
    // Stop checking status when the component is destroyed.
    clearInterval(this.checkStatusIntervalHolder);

    // If there is an active payment, cancel it.
    // Note that in a typical close attempt, handleCloseAttempt will have
    // already set paymentId to null. This is a backup in case the component
    // is destroyed via a pathway other than handleCloseAttempt.
    if (this.paymentId) {
      this.paymentService
        .cancelStripeTerminalAction(this.selectedReader.id)
        .then(() => {
          this.paymentService.cancelPayment(this.paymentId);
        })
    }
  }

  public handleCloseAttempt() {
    // If there is no active payment, just close the modal.
    if (!this.paymentId) {
      this.activeModal.close();
      return;
    }

    // The server already refused the cancel: leave the payment to the poll and
    // webhook, and clear paymentId so ngOnDestroy does not try again.
    if (this.cancelRefused) {
      this.paymentId = null;
      this.activeModal.close();
      return;
    }

    // If there is an active payment, cancel the terminal action and payment before closing.
    this.loadingCancelPayment = true;
    this.cancelInProgress = true;
    this.errorMessage = '';

    this.paymentService
      .cancelStripeTerminalAction(this.selectedReader.id)
      .then(() => this.paymentService.cancelPayment(this.paymentId))
      .then(paymentStatus => {
        // The status poll may have closed the modal while the cancel was in flight.
        if (!this.paymentId) { return; }

        if (paymentStatus === PaymentStatus.paymentComplete) {
          this.paymentId = null;
          this.activeModal.close();
          this.onSuccess();
        } else if (paymentStatus === PaymentStatus.paymentPending) {
          this.cancelRefused = true;
          this.errorMessage = 'This payment could not be cancelled. The reader may still be processing it; wait for it to finish, or close this window and the payment will be reconciled automatically.';
        } else {
          this.paymentId = null;
          this.activeModal.close();
        }
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else if (reason.status === 400) {
          this.errorMessage = 'Unable to cancel payment: ' + reason.error;
        } else {
          this.serverError = reason.status;
        }
      }).finally(() => {
        this.loadingCancelPayment = false;
        this.cancelInProgress = false;
      });
  }

  public readerResultsPresent() {
    return this.readerList && (this.readerList.length > 0);
  }

  public selectReader(reader: StripeReader) {
    this.selectedReader = reader;
    this.initializePayment();
  }

  public initializePayment(options: liveReaderRefusal.LiveReaderActionOptions = {}
                           ): Promise<liveReaderRefusal.LiveReaderCheckOutcome> {
    this.serverError = null;
    this.errorMessage = '';
    if (!options.recheck) { this.liveReaderRefusal = null; }
    this.cancelRefused = false;

    this.state = ModalState.initializingPayment;

    return this.paymentService
      .initStripeTerminalPayment(
        this.invoiceId,
        this.customerId,
        this.paymentDescription,
        this.selectedReader.id,
        this.amount,
        options.bypass
      )
      .then((result): liveReaderRefusal.LiveReaderCheckOutcome => {
        this.paymentId = result.payment_id;
        this.state = ModalState.waitingForPayment;
        return 'cleared';
      })
      .catch((reason): liveReaderRefusal.LiveReaderCheckOutcome => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else if (liveReaderRefusal.isLiveReaderUnconfirmedError(reason)) {
          this.state = ModalState.choosingReader;
          if (options.recheck) { return 'live'; }
          const refusal: liveReaderRefusal.LiveReaderRefusal = {
            message: liveReaderRefusal.getErrorMessageText(reason),
            checkAgain: () => this.initializePayment({ recheck: true }),
            checking: false,
          };
          liveReaderRefusal.openProceedAnywayModal(
            this.modalService,
            refusal,
            () => this.initializePayment({ bypass: true }),
            () => { this.liveReaderRefusal = refusal; }
          );
        } else if (liveReaderRefusal.isLiveReaderCapturedError(reason)) {
          liveReaderRefusal.openAlreadyPaidModal(
            this.modalService,
            liveReaderRefusal.getErrorMessageText(reason)
          );
          this.paymentId = null;
          this.activeModal.close();
          this.onSuccess();
        } else if (reason.status === 400) {
          this.errorMessage = reason.error;
          this.state = ModalState.choosingReader;
        } else {
          this.serverError = reason.status;
        }
        return 'other';
      })
      .then(outcome => {
        if (outcome !== 'live') { this.liveReaderRefusal = null; }
        return outcome;
      });
  }

  private loadReaders() {
    this.loadingReaders = true;

    this.serverError = null;
    this.errorMessage = '';

    this.locationService
      .getTerminals(this.accountService.getMyAccount().default_gym_id)
      .then(response => { this.readerList = response; })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingReaders = false; });
  }

  private checkPaymentStatus() {
    if (this.state !== ModalState.waitingForPayment) { return; }
    if (!this.paymentId) { return; }

    this.paymentService
      .checkStripeTerminalPayment(this.paymentId)
      .then(({ status, payment }) => {
        if (this.state !== ModalState.waitingForPayment) { return; }
        if (!this.paymentId) { return; }

        if (status === StripeTerminalPaymentStatus.succeeded) {
          // Decimals arrive as strings, so compare as numbers.
          const partialApproval = payment.amount_requested != null &&
            Number(payment.amount_requested) > Number(payment.amount);
          if (partialApproval) {
            this.openPartialApprovalModal(payment);
          }
          this.paymentId = null;
          this.activeModal.close();
          this.onSuccess();
        } else if (status === StripeTerminalPaymentStatus.failed) {
          // The poll can see the cancel's own CANCELLED before the cancel
          // promise resolves; let handleCloseAttempt decide instead.
          if (this.cancelInProgress) { return; }
          this.paymentId = null;
          this.state = ModalState.paymentFailed;
        }
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else {
          this.serverError = reason.status;
        }
      });
  }

  // The card approved less than requested (PRO-1608); the rest stays due on the sale.
  private openPartialApprovalModal(payment: Payment) {
    const approved = Number(payment.amount);
    const requested = Number(payment.amount_requested);
    const modalRef = this.modalService.open(SimpleMessageModalComponent);
    modalRef.componentInstance.headerText = 'Partial Approval';
    modalRef.componentInstance.messageText =
      'The card covered only ' + this.orgConfig.formatCurrency(approved) +
      ' of the ' + this.orgConfig.formatCurrency(requested) + ' requested. ' +
      this.orgConfig.formatCurrency(requested - approved) + ' was not collected and is still owed.';
    modalRef.componentInstance.messageAlertClass = 'alert-warning';
  }
}
